import { builtinLayerId, createBuiltinLayers, defaultLayerKeyFor } from './layers.js';
import { newId } from './ids.js';
import type { BoardData, Frame, Item, ItemKind } from './schema.js';
import { SCHEMA_VERSION } from './schema.js';

export const DEFAULT_STAGE_ID = 'frame:stage';

export function createBoardData(
  name: string,
  opts: { width?: number; depth?: number } = {},
): BoardData {
  const frame: Frame = {
    id: DEFAULT_STAGE_ID,
    kind: 'stage',
    name: 'Stage',
    x: 0,
    y: 0,
    width: opts.width ?? 10,
    depth: opts.depth ?? 6,
    zones: true,
  };
  return {
    meta: { schemaVersion: SCHEMA_VERSION, name, units: 'metric' },
    frames: { [frame.id]: frame },
    layers: Object.fromEntries(createBuiltinLayers().map((l) => [l.id, l])),
    items: {},
    connections: {},
    channels: {},
    mixes: {},
    power: {},
    freeform: {},
    ink: {},
  };
}

/** Item with sane defaults; the caller can override everything. */
export function makeItem(kind: ItemKind, overrides: Partial<Item> = {}): Item {
  return {
    id: newId(),
    kind,
    layerId: builtinLayerId(defaultLayerKeyFor(kind)),
    label: '',
    frameId: DEFAULT_STAGE_ID,
    x: 0,
    y: 0,
    // a new mic points its pickup (+y of the symbol) to the stage, not the audience (OIL-113)
    rotation: kind === 'microphone' ? 180 : 0,
    tags: [],
    props: {},
    ...overrides,
  };
}

/** Default area names in the UI language ("Bühne", "Bereich 2", …); other names as typed. */
export function frameTitle(name: string, locale: 'en' | 'de' = 'en'): string {
  if (locale !== 'de') return name;
  const n = name.trim();
  const m = /^(Stage|Monitor world|Area)(?: (\d+))?$/.exec(n);
  if (m === null || (m[1] === 'Area' && m[2] === undefined)) return name;
  const base = m[1] === 'Stage' ? 'Bühne' : m[1] === 'Area' ? 'Bereich' : 'Monitorplatz';
  return m[2] === undefined ? base : `${base} ${m[2]}`;
}
